/**
 * Flow detail — overview, publish helper (copy/download JSON + paste Flow ID),
 * live preview, and Send/Run. Because flows can't be published via API, the publish
 * section walks the admin through doing it in the hub, then storing the Flow ID.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconRocket, IconEdit, IconSend, IconTrash, IconCopy, IconDownload, IconCode, IconCircleCheck, IconCopyPlus,
} from '@tabler/icons-react';
import {
  Stack, Group, Text, Title, Badge, Button, Loader, Alert, TextInput, Collapse, Code, SegmentedControl, Divider,
  SimpleGrid, ScrollArea,
} from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import { ConfirmDeleteModal } from '../../../../../components/ui/overlays';
import { toast } from '../../../../../components/ui/providers/AlertProvider';
import { ChannelPage } from '../../_shared/ChannelPage';
import { FlowScreenPreview } from '../../_shared/FlowScreenPreview';
import { FlowSendDrawer } from './FlowSendDrawer';
import { useWhatsAppFlows, type FlowView } from '../../_shared/useWhatsAppFlows';
import { parseDefinition } from '../../_shared/flowDefinition';
import { FLOW_STATUS, fmtFlowDate } from './FlowsListPage';

const BASE = '/admin/communications/whatsapp/flows';
const HUB_URL = 'https://hub.360dialog.com';

export function FlowDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const flowId = Number(id);
  const { saving, getFlow, deleteFlow, publishFlow, createFlow } = useWhatsAppFlows();

  const [loading, setLoading] = useState(false);
  const [flow, setFlow] = useState<FlowView | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [metaFlowId, setMetaFlowId] = useState('');
  const [showJson, setShowJson] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const f = await getFlow(flowId);
    setFlow(f);
    setMetaFlowId(f?.metaFlowId ?? '');
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [flowId]);

  const screens = useMemo(() => parseDefinition(flow?.definition), [flow?.definition]);
  const activeScreen = screens.find((s) => s.id === activeId) ?? screens[0];
  const activeIndex = screens.findIndex((s) => s.id === activeScreen?.id);
  const published = !!flow?.metaFlowId;

  const copyJson = async () => {
    try { await navigator.clipboard.writeText(flow?.flowJson || ''); toast.success('Flow JSON copied'); }
    catch { toast.error('Copy failed'); }
  };
  const downloadJson = () => {
    const blob = new Blob([flow?.flowJson || ''], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `flow-${(flow?.name || 'whatsapp').replace(/\s+/g, '-').toLowerCase()}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const handlePublish = async () => {
    const updated = await publishFlow(flowId, metaFlowId.trim());
    if (updated) setFlow(updated);
  };
  const handleDelete = async () => {
    if (await deleteFlow(flowId)) navigate(BASE);
  };
  const handleDuplicate = async () => {
    if (!flow) return;
    const copy = await createFlow({
      name: `${flow.name} (copy)`, description: flow.description, category: flow.category,
      definition: flow.definition, flowJson: flow.flowJson, screenCount: flow.screenCount,
      defaultBodyText: flow.defaultBodyText, defaultCta: flow.defaultCta,
      defaultHeaderText: flow.defaultHeaderText, defaultFooterText: flow.defaultFooterText, flowAction: flow.flowAction,
    });
    if (copy) navigate(`${BASE}/${copy.flowId}`);
  };

  if (loading && !flow) {
    return (
      <ChannelPage icon={<IconRocket size={28} />} title="Flows" onBack={() => navigate(BASE)}>
        <Group justify="center" p="xl"><Loader size="lg" /></Group>
      </ChannelPage>
    );
  }
  if (!flow) {
    return (
      <ChannelPage icon={<IconRocket size={28} />} title="Flows" onBack={() => navigate(BASE)}>
        <Alert color="red">Flow not found.</Alert>
      </ChannelPage>
    );
  }

  const actions = (
    <Group gap="xs">
      <Button variant="light" size="xs" leftSection={<IconEdit size={14} />} onClick={() => navigate(`${BASE}/${flowId}/edit`)}>Edit</Button>
      <Button variant="light" size="xs" leftSection={<IconCopyPlus size={14} />} onClick={handleDuplicate}>Duplicate</Button>
      <Button size="xs" leftSection={<IconSend size={14} />} onClick={() => setSendOpen(true)} disabled={!published} gradientVariant="primary">Send</Button>
      <Button variant="subtle" size="xs" color="red" leftSection={<IconTrash size={14} />} onClick={() => setDeleteOpen(true)}>Delete</Button>
    </Group>
  );

  return (
    <ChannelPage icon={<IconRocket size={28} />} title="Flows" subtitle={flow.name} onBack={() => navigate(BASE)} actions={actions}>
      <Stack gap="md">
        <Group gap="sm">
          <Title order={4}>{flow.name}</Title>
          <Badge variant="light" color={FLOW_STATUS[flow.statusId]?.color}>{FLOW_STATUS[flow.statusId]?.label ?? flow.statusId}</Badge>
          {flow.category && <Badge variant="light" color="grape">{flow.category}</Badge>}
        </Group>
        {flow.description && <Text size="sm" c="dimmed">{flow.description}</Text>}
        <Text size="xs" c="dimmed">
          {screens.length} screen{screens.length === 1 ? '' : 's'} · updated {fmtFlowDate(flow.modifiedDateTime)}
          {flow.metaFlowId ? ` · Flow ID ${flow.metaFlowId}` : ''}
        </Text>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          {/* Publish helper */}
          <Card withBorder padding="md">
            <Stack gap="sm">
              <Group gap="xs">
                {published ? <IconCircleCheck size={18} color="var(--mantine-color-green-6)" /> : <IconRocket size={18} />}
                <Text fw={500}>{published ? 'Published' : 'Publish this flow'}</Text>
              </Group>

              {!published && (
                <Alert color="blue">
                  Flows are published in the 360dialog/Meta hub (not from here). Follow these steps once:
                  <Stack gap={2} mt={6}>
                    <Text size="xs">1. <b>Copy</b> or <b>Download</b> the Flow JSON below.</Text>
                    <Text size="xs">2. In the <b>360dialog/Meta hub</b> → WhatsApp Manager → Flows → <b>Create</b>, paste the JSON & publish.</Text>
                    <Text size="xs">3. Copy the <b>Flow ID</b> the hub gives you.</Text>
                    <Text size="xs">4. Paste it below and <b>Save</b> — the flow becomes sendable.</Text>
                  </Stack>
                </Alert>
              )}

              <Group gap="xs">
                <Button variant="light" size="xs" leftSection={<IconCopy size={14} />} onClick={copyJson}>Copy JSON</Button>
                <Button variant="light" size="xs" leftSection={<IconDownload size={14} />} onClick={downloadJson}>Download JSON</Button>
                <Button variant="subtle" size="xs" component="a" href={HUB_URL} target="_blank" rel="noreferrer">Open hub ↗</Button>
              </Group>

              <Divider />
              <Group align="flex-end" gap="xs">
                <TextInput style={{ flex: 1 }} label="Meta Flow ID" placeholder="e.g. 1234567890" value={metaFlowId} onChange={(e) => setMetaFlowId(e.currentTarget.value)} />
                <Button onClick={handlePublish} loading={saving}>{metaFlowId.trim() ? 'Save & publish' : 'Save'}</Button>
              </Group>
              {published && <Text size="xs" c="green">Ready to send. Use the <b>Send</b> button above.</Text>}
            </Stack>
          </Card>

          {/* Preview */}
          <Card withBorder padding="md">
            <Group justify="space-between" mb="sm">
              <Text size="sm" fw={500}>Preview</Text>
              {screens.length > 1 && (
                <SegmentedControl size="xs" value={activeScreen?.id} onChange={setActiveId} data={screens.map((s, i) => ({ value: s.id, label: String(i + 1) }))} />
              )}
            </Group>
            {activeScreen && <FlowScreenPreview screen={activeScreen} flowName={flow.name} screenIndex={activeIndex} screenTotal={screens.length} />}
          </Card>
        </SimpleGrid>

        {/* Advanced JSON */}
        <Card withBorder padding="sm">
          <Group justify="space-between">
            <Group gap="xs"><IconCode size={16} /><Text size="sm" fw={500}>Advanced: Flow JSON</Text></Group>
            <Button size="xs" variant="subtle" onClick={() => setShowJson((v) => !v)}>{showJson ? 'Hide' : 'Show'}</Button>
          </Group>
          <Collapse in={showJson}>
            <ScrollArea h={320} mt="xs">
              <Code block style={{ fontSize: 11 }}>{flow.flowJson || '{}'}</Code>
            </ScrollArea>
          </Collapse>
        </Card>
      </Stack>

      <FlowSendDrawer flow={flow} opened={sendOpen} onClose={() => setSendOpen(false)} />
      <ConfirmDeleteModal
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete flow"
        message="This removes the flow from CCMS. Any copy already published in the hub is unaffected."
        itemName={flow.name}
        loading={saving}
      />
    </ChannelPage>
  );
}
